import { useCallback } from 'react'
import { useSubmitMessage } from '@renderer/chat'
import { GlobalEvents, GlobalStore } from '@highlight/ipc'
import { CalendarEvent, AudioNote } from '@highlight/common'
import { mapTranscriptEntriesToTranscriptString } from '@renderer/chat/utils/audioNotes'

/**
 * Hook to prepare for a scheduled meeting by creating a chat with context
 */
export const usePrepareMeeting = (onNewOrOpenChat?: () => void) => {
  const submitMessage = useSubmitMessage()

  const prepareMeeting = useCallback(
    (meeting: CalendarEvent & { linkedAudioNotes: AudioNote[] }) => {
      // Open a new chat if callback is provided
      if (onNewOrOpenChat) {
        onNewOrOpenChat()
      }

      const isRecurringMeeting = meeting.linkedAudioNotes.length > 0
      const recurringMeetingPrompt =
        'This is a recurring meeting. Analyze the previous meetings to catch me up and prepare me for the meeting, and let me know anything I should think of/about before the meeting.'
      const externalMeetingPrompt =
        'Do some research on each attendee by searching the web and provide me with a summary of each attendee, and anything I should think of/about before the meeting.'
      const prompt =
        `Help me prepare for this meeting: ${meeting.title}\n` +
        `In the meeting, I will be joined by ${meeting.attendees.map((attendee) => attendee.email).join(', ')}.\n` +
        (isRecurringMeeting ? recurringMeetingPrompt : externalMeetingPrompt)

      submitMessage(
        prompt,
        isRecurringMeeting
          ? {
              attachments: meeting.linkedAudioNotes.map((audioNote) => ({
                attachment: {
                  type: 'audioNote',
                  title: audioNote.title,
                  startTime: audioNote.startedAt.toISOString(),
                  text: mapTranscriptEntriesToTranscriptString(audioNote.transcriptEntries),
                },
              })),
              isNewChat: true,
            }
          : {
              isNewChat: true,
            },
      )

      // Listen for the chat creation event to store the preparation chat ID
      const handleChatSent = (data: { chatId: string; conversationId: string }) => {
        GlobalStore.conversations().set(`meeting.${meeting.id}.preparation`, {
          chatId: data.conversationId,
          createdAt: Date.now(),
          meetingStart: new Date(meeting.start).getTime(),
          meetingEnd: new Date(meeting.end).getTime(),
        })
        GlobalEvents.off('chat:message:sent', handleChatSent)
      }
      GlobalEvents.on('chat:message:sent', handleChatSent)
    },
    [onNewOrOpenChat, submitMessage],
  )

  return prepareMeeting
}
